import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.tsx';
import announcementsApi, { Announcement } from '../../services/announcementsApi.ts';
import './UpdatesPage.css';

const tagLabel: Record<string, string> = {
  INFO: 'Info',
  IMPORTANT: 'Important',
  WARNING: 'Warning',
};

const IST = 'Asia/Kolkata';

const toISTDate = (iso: string) => {
  // Convert UTC ISO string to IST date parts for comparison
  const parts = new Intl.DateTimeFormat('en-IN', {
    timeZone: IST, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date(iso));
  const get = (type: string) => parseInt(parts.find(p => p.type === type)!.value);
  return new Date(get('year'), get('month') - 1, get('day'));
};

const formatDateKey = (iso: string) => {
  const d = toISTDate(iso);
  const today = toISTDate(new Date().toISOString());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (d.getTime() === today.getTime()) return 'Today';
  if (d.getTime() === yesterday.getTime()) return 'Yesterday';

  return new Date(iso).toLocaleDateString('en-IN', {
    timeZone: IST, day: '2-digit', month: 'long', year: 'numeric',
  });
};

const formatTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-IN', {
    timeZone: IST, hour: '2-digit', minute: '2-digit', hour12: true,
  });

const groupByDate = (items: Announcement[]) => {
  const groups: { dateLabel: string; items: Announcement[] }[] = [];
  const seen: Record<string, number> = {};

  items.forEach(item => {
    const label = formatDateKey(item.createdAt);
    if (seen[label] === undefined) {
      seen[label] = groups.length;
      groups.push({ dateLabel: label, items: [] });
    }
    groups[seen[label]].items.push(item);
  });

  return groups;
};

const AnnouncementCard: React.FC<{ item: Announcement }> = ({ item }) => (
  <div className={`update-card tag-${item.tag.toLowerCase()} ${item.isPinned ? 'pinned' : ''}`}>
    <div className="update-card-header">
      <div className="update-card-meta">
        <span className={`update-tag tag-badge-${item.tag.toLowerCase()}`}>{tagLabel[item.tag]}</span>
        {item.isPinned && <span className="update-pin-badge">📌 Pinned</span>}
      </div>
      <span className="update-time">{formatDateKey(item.createdAt)} · {formatTime(item.createdAt)}</span>
    </div>
    <h3 className="update-card-title">{item.title}</h3>
    <div className="update-card-content" dangerouslySetInnerHTML={{ __html: item.content }} />
    {item.videoUrl && (
      <div className="update-video-wrap">
        <video src={item.videoUrl} controls className="update-video-player" controlsList="nodownload" />
      </div>
    )}
  </div>
);

const UpdatesPage: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAuthenticated) { navigate('/'); return; }
    if (!user?.isPremium) { navigate('/pricing'); return; }
    announcementsApi.getAnnouncements()
      .then(data => setAnnouncements(data))
      .catch(() => setError('Failed to load updates. Please try again.'))
      .finally(() => setLoading(false));
  }, [isAuthenticated, user, navigate]);

  const pinned = announcements.filter(a => a.isPinned);
  const unpinned = announcements.filter(a => !a.isPinned);
  const groups = groupByDate(unpinned);

  return (
    <div className="updates-page">
      <div className="updates-header">
        <h1 className="updates-title">Updates & Announcements</h1>
        <p className="updates-subtitle">Latest news, feature updates, and important notices from SwingSetup AI</p>
      </div>

      {loading && (
        <div className="updates-loading">
          <div className="updates-spinner" />
          <span>Loading updates…</span>
        </div>
      )}

      {error && <div className="updates-error">{error}</div>}

      {!loading && !error && announcements.length === 0 && (
        <div className="updates-empty">
          <div className="updates-empty-icon">📋</div>
          <p>No updates yet. Check back soon!</p>
        </div>
      )}

      <div className="updates-feed">

        {/* Pinned section — always at top regardless of date */}
        {pinned.length > 0 && (
          <div className="updates-group">
            <div className="updates-date-separator">
              <span className="updates-date-label updates-pinned-label">📌 Pinned</span>
            </div>
            <div className="updates-group-cards">
              {pinned.map(item => <AnnouncementCard key={item.id} item={item} />)}
            </div>
          </div>
        )}

        {/* Date-grouped unpinned posts */}
        {groups.map(group => (
          <div key={group.dateLabel} className="updates-group">
            <div className="updates-date-separator">
              <span className="updates-date-label">{group.dateLabel}</span>
            </div>
            <div className="updates-group-cards">
              {group.items.map(item => <AnnouncementCard key={item.id} item={item} />)}
            </div>
          </div>
        ))}

      </div>
    </div>
  );
};

export default UpdatesPage;
